import React, { useState, useEffect, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Chip,
    Box,
    Typography,
    CircularProgress,
    Alert,
} from "@mui/material";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";

const UploadToClassMaterialModal = ({ open, onClose, report, localStudent, ettUserId, testId }) => {
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState("");
    const [selectedSections, setSelectedSections] = useState([]);
    const [title, setTitle] = useState("");
    const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchingCourses, setFetchingCourses] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const availableSections = useMemo(() => {
        if (!report) return [];
        // Get unique subjects from actual report sections
        const subjects = [];
        report.session_sections.forEach(s => {
            if (!subjects.includes(s.subject)) subjects.push(s.subject);
        });
        return subjects;
    }, [report]);

    const showSectionSelector = useMemo(() => {
        return availableSections.length >= 2;
    }, [availableSections]);

    const generateTitle = (sections) => {
        if (!report || !localStudent) return "";
        const studentName = `${localStudent.first_name}_${localStudent.last_name}`;
        const examTitle = report.test_title.replace(/\s+/g, '_');

        if (sections.length === availableSections.length || sections.length === 0) {
            return `${examTitle}_${studentName}_result_share.pdf`;
        }
        const sectionStr = sections.join('_').replace(/[\s&]+/g, '_');
        return `${examTitle}_${studentName}_${sectionStr}_result_share.pdf`;
    };

    // Fetch courses and initialize state when modal opens
    useEffect(() => {
        if (open && localStudent) {
            setFetchingCourses(true);
            setError("");
            setSuccess("");
            setSelectedCourse("");
            setTitleManuallyEdited(false);

            Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/local-student/${localStudent.id}/courses`,
                { headers: { Authorization: getAuthHeader() } }
            ).then((response) => {
                setCourses(response.data);
            }).catch((err) => {
                console.error("Fetch courses error:", err);
                setCourses([]);
            }).finally(() => {
                setFetchingCourses(false);
            });

            // Initialize sections
            setSelectedSections([...availableSections]);
            setTitle(generateTitle(availableSections));
        }
    }, [open]);

    // Update title when sections change (unless manually edited)
    useEffect(() => {
        if (!titleManuallyEdited) {
            setTitle(generateTitle(selectedSections));
        }
    }, [selectedSections]);

    const handleSectionToggle = (section) => {
        setSelectedSections(prev => {
            if (prev.includes(section)) {
                return prev.filter(s => s !== section);
            }
            return [...prev, section];
        });
    };

    const handleTitleChange = (e) => {
        setTitle(e.target.value);
        setTitleManuallyEdited(true);
    };

    const handleConfirm = async () => {
        if (!selectedCourse) {
            setError("Please select a course");
            return;
        }
        if (selectedSections.length === 0) {
            setError("Please select at least one section");
            return;
        }
        if (!title.trim()) {
            setError("Please enter a title");
            return;
        }

        setLoading(true);
        setError("");

        try {
            await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/ett-reports/upload-to-class-material`,
                {
                    ettUserId,
                    testId,
                    courseUuid: selectedCourse,
                    selectedSections,
                    title: title.trim(),
                },
                { headers: { Authorization: getAuthHeader() } }
            );
            setSuccess("Report uploaded to class materials successfully!");
        } catch (err) {
            console.error("Upload error:", err);
            setError(err.response?.data?.error || err.response?.data?.message || "Failed to upload report");
        } finally {
            setLoading(false);
        }
    };

    const sortedCourses = useMemo(() => {
        return [...courses].sort((a, b) => {
            if (a.isCurrent && !b.isCurrent) return -1;
            if (!a.isCurrent && b.isCurrent) return 1;
            return (b.end_date || "").localeCompare(a.end_date || "");
        });
    }, [courses]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Report to Class Material</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && (
                    <Alert
                        severity="success"
                        sx={{ mb: 2 }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    window.open(`/classrooms/${selectedCourse}/class-materials`, '_blank');
                                }}
                            >
                                View
                            </Button>
                        }
                    >
                        {success}
                    </Alert>
                )}

                {/* Course Selector */}
                <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                    <InputLabel>Select Course</InputLabel>
                    <Select
                        value={selectedCourse}
                        onChange={(e) => setSelectedCourse(e.target.value)}
                        label="Select Course"
                        disabled={fetchingCourses || loading}
                    >
                        {fetchingCourses ? (
                            <MenuItem disabled>
                                <CircularProgress size={20} sx={{ mr: 1 }} /> Loading courses...
                            </MenuItem>
                        ) : sortedCourses.length === 0 ? (
                            <MenuItem disabled>No courses found</MenuItem>
                        ) : (
                            sortedCourses.map((course) => (
                                <MenuItem key={course.uuid} value={course.uuid}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        {course.course_title}
                                        {course.isCurrent && (
                                            <Chip label="Current" size="small" color="success" />
                                        )}
                                    </Box>
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>

                {/* Section Selector */}
                {showSectionSelector && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" gutterBottom>
                            Sections to include
                        </Typography>
                        <FormGroup row>
                            {availableSections.map((section) => (
                                <FormControlLabel
                                    key={section}
                                    control={
                                        <Checkbox
                                            checked={selectedSections.includes(section)}
                                            onChange={() => handleSectionToggle(section)}
                                            disabled={loading}
                                        />
                                    }
                                    label={section}
                                />
                            ))}
                        </FormGroup>
                    </Box>
                )}

                {/* Title Input */}
                <TextField
                    fullWidth
                    label="File Name"
                    value={title}
                    onChange={handleTitleChange}
                    disabled={loading}
                    helperText="Auto-generated based on selections. You can edit manually."
                    sx={{ mb: 1 }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    {success ? "Close" : "Cancel"}
                </Button>
                {!success && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        disabled={loading || !selectedCourse || selectedSections.length === 0 || !title.trim()}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                    >
                        {loading ? "Uploading..." : "Confirm Upload"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default UploadToClassMaterialModal;
